'use client';
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Button, TextField, Typography, alpha, useTheme, CircularProgress, IconButton, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';
import { Icon } from '@iconify/react';
import { Additem } from 'iconsax-react';
import { useAppDispatch, useAppSelector } from '../../../application/store/useStore';
import { useCompanyContext } from '../../../application/context/CompanyContext';
import { CustomGrid } from '../../components/common/dataGrid';
import { getKnowledgeBasesAsync, deleteKnowledgeBaseAsync } from '../../../application/features/knowledgeBase/knowledgeBaseThunk';
import { formatDMY } from '../../../infrastructure/helpers';
import { DeleteDialog } from '../proposals/delete-proposal-dialog';
import NewKnowledgeBaseDialog from './new-knowledge-base-dialog';
import EditKnowledgeBaseDialog from './edit-knowledge-base-dialog';
import KnowledgeBaseDocumentPopUp from './knowledge-base-document-popup';
import { toast } from 'react-toastify';
import { Warning2 } from 'iconsax-react';
import { Delete } from 'react-iconly';
const KnowledgeBase = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const { selectedCompanyId } = useCompanyContext();
    const { allKnowledgeBases } = useAppSelector((state) => state.knowledgeBase);
    const { currentUser } = useAppSelector((state) => state.auth);
    const { allUsers } = useAppSelector((state) => state.users);
    const [toastMessage, setToastMessage] = useState('');
    const [selectedKnowledgeBaseId, setSelectedKnowledgeBaseId] = useState(null);
    const [selectedKnowledgeBase, setSelectedKnowledgeBase] = useState(null);
    const [selectedKnowledgeBaseTitle, setSelectedKnowledgeBaseTitle] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [newKnowledgeBaseDialog, setNewKnowledgeBaseDialog] = useState(false);
    const [editKnowledgeBaseDialog, setEditKnowledgeBaseDialog] = useState(false);
    const [viewDocumentsDialog, setViewDocumentsDialog] = useState(false);
    const dispatch = useAppDispatch();
    const [sortModel, setSortModel] = useState([
        { field: 'uploadDate', sort: 'desc' }
    ]);
    const openDeleteDialog = (id, title) => {
        setSelectedKnowledgeBaseId(id);
        setSelectedKnowledgeBaseTitle(title);
        setIsDeleteDialogOpen(true);
    };
    const closeDeleteDialog = () => {
        setSelectedKnowledgeBaseId(null);
        setSelectedKnowledgeBaseTitle(null);
        setIsDeleteDialogOpen(false);
    };
    const closeNewKnowledgeBaseDialog = () => {
        setNewKnowledgeBaseDialog(false);
    };
    const openEditDialog = (knowledgeBase) => {
        setSelectedKnowledgeBase(knowledgeBase);
        setEditKnowledgeBaseDialog(true);
    };
    const closeEditDialog = () => {
        setSelectedKnowledgeBase(null);
        setEditKnowledgeBaseDialog(false);
    };
    const closeViewDocumentsDialog = () => {
        setViewDocumentsDialog(false);
    };
    const handleNewKnowledgeBaseClick = () => {
        if (!selectedCompanyId || selectedCompanyId === 'ALL') {
            toast.warning(t('selectCompanyToUploadDocument') || 'Please select a company to upload the document');
            return;
        }
        setNewKnowledgeBaseDialog(true);
    };
    const columns = [
        {
            field: 'uploadedDate',
            headerName: t('uploadDate'),
            flex: 1,
            minWidth: 150,
            renderCell: (params) => {
                return _jsx("span", { children: formatDMY(params.value) });
            }
        },
        {
            field: 'title',
            headerName: t('title'),
            flex: 1,
            minWidth: 200,
            renderCell: (params) => _jsx("div", { children: params.value || 'N/A' })
        },
        {
            field: 'expertise',
            headerName: t('expertise'),
            flex: 1,
            minWidth: 240,
            valueGetter: (value, row) => {
                return row.expertiseIdList?.map((e) => e.expertiseName).join(', ') || 'N/A';
            }
        },
        {
            field: 'subExpertise',
            headerName: t('subExpertise'),
            flex: 1,
            minWidth: 200,
            valueGetter: (value, row) => {
                const subExpertises = row.expertiseIdList
                    ?.map((e) => e.subExpertises)
                    .filter(Boolean)
                    .join(', ');
                return subExpertises || 'N/A';
            }
        },
        {
            field: 'companyName',
            headerName: t('company'),
            flex: 1,
            minWidth: 180,
            renderCell: (params) => _jsx("div", { style: { color: '#242424' }, children: params.value || 'N/A' })
        },
        {
            field: 'files',
            headerName: t('documentCount'),
            flex: 1,
            minWidth: 110,
            renderCell: (params) => {
                const row = params.row;
                const docCount = row.files?.length || 0;
                return (_jsx(Button, { size: 'small', sx: {
                        borderRadius: '500px',
                        minWidth: '24px',
                        backgroundColor: alpha(theme.palette.grey[500], 0.16),
                        color: theme.palette.text.primary,
                        boxShadow: 'none'
                    }, onClick: () => viewDocuments(params.row.id, row.title || 'Knowledge Base'), variant: 'contained', children: _jsx(Typography, { variant: 'body2', fontWeight: 400, children: docCount }) }));
            }
        },
        {
            field: 'actions',
            headerName: '',
            flex: 1,
            align: 'center',
            minWidth: 200,
            renderCell: (params) => {
                const row = params.row;
                const status = row.status?.toString();
                const isInProgress = status === 'InProgress';
                const isFailed = status === 'Failed';
                return (_jsxs(Box, { sx: {
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        width: '100%',
                        gap: '8px'
                    }, children: [isInProgress && _jsx(CircularProgress, { size: 20, sx: { mr: 1 } }), isFailed && (_jsx(IconButton, { size: 'small', children: _jsx(Warning2, { color: 'red' }) })), _jsx(IconButton, { size: 'small', onClick: () => openEditDialog(params.row), sx: {
                                color: theme.palette.primary.main,
                                '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.1) }
                            }, children: _jsx(Icon, { icon: 'mdi:pencil', width: 20, height: 20 }) }), _jsx(IconButton, { size: 'small', onClick: () => deleteKnowledgeBase(params.row.id, params.row.title), children: _jsx(Delete, { set: 'curved', primaryColor: theme.palette.error.main }) })] }));
            }
        }
    ];
    useEffect(() => {
        console.log('=== Knowledge Base Screen - Fetch Triggered ===');
        console.log('Selected Company ID:', selectedCompanyId);
        if (!selectedCompanyId) {
            console.log('No company selected, waiting...');
            return;
        }
        console.log('Fetching knowledge bases for company:', selectedCompanyId);
        dispatch(getKnowledgeBasesAsync(selectedCompanyId))
            .unwrap()
            .then((data) => {
            console.log('Knowledge Bases received:', data?.length || 0);
        });
    }, [dispatch, selectedCompanyId]);
    useEffect(() => {
        if (toastMessage) {
            setTimeout(() => setToastMessage(''), 3000);
        }
    }, [toastMessage]);
    const viewDocuments = (knowledgeBaseId, title) => {
        setSelectedKnowledgeBaseId(knowledgeBaseId);
        setSelectedKnowledgeBaseTitle(title);
        setViewDocumentsDialog(true);
        // Files are already in the knowledge base object, no need to fetch separately
    };
    const handleDelete = () => {
        if (selectedKnowledgeBaseId) {
            dispatch(deleteKnowledgeBaseAsync(selectedKnowledgeBaseId))
                .unwrap()
                .then(() => {
                setToastMessage('Knowledge Base deleted successfully');
                dispatch(getKnowledgeBasesAsync(selectedCompanyId || undefined));
                closeDeleteDialog();
            })
                .catch(() => {
                setToastMessage('Failed to delete Knowledge Base');
            });
        }
    };
    const deleteKnowledgeBase = (id, title) => {
        openDeleteDialog(id, title);
    };
    // Get user's assigned company IDs for Admin/User role filtering
    const userAssignedCompanyIds = useMemo(() => {
        if (!currentUser || currentUser.role === 'SuperAdmin')
            return null;
        const currentUserDetails = allUsers?.find((u) => u.email === currentUser.email);
        return currentUserDetails?.companyIds || [];
    }, [currentUser, allUsers]);
    // Filter knowledge bases based on user role, selected company AND search term
    const filteredKnowledgeBases = (allKnowledgeBases || []).filter((kb) => {
        // FIRST: Role-based filtering for Admin and User
        if (currentUser?.role !== 'SuperAdmin' && userAssignedCompanyIds) {
            if (kb.companyId && !userAssignedCompanyIds.includes(kb.companyId)) {
                return false;
            }
        }
        // SECOND: Filter by selected company
        if (selectedCompanyId && selectedCompanyId !== 'ALL') {
            if (kb.companyId !== selectedCompanyId) {
                return false;
            }
        }
        // THIRD: Filter by search term
        if (!searchTerm.trim())
            return true;
        const searchLower = searchTerm.toLowerCase();
        const expertiseNames = kb.expertiseIdList
            ?.map((e) => e.expertiseName)
            .join(' ')
            .toLowerCase() || '';
        return (kb.title?.toLowerCase().includes(searchLower) ||
            kb.description?.toLowerCase().includes(searchLower) ||
            expertiseNames.includes(searchLower) ||
            kb.companyName?.toLowerCase().includes(searchLower));
    });
    return (_jsxs(_Fragment, { children: [_jsxs(Box, { width: '100%', display: 'flex', flexDirection: 'column', gap: 1.5, paddingY: 1, children: [_jsxs(Box, { display: 'flex', justifyContent: 'space-between', alignItems: 'center', children: [_jsx(Typography, { variant: 'h4', fontWeight: 600, children: t('knowledgeBase') }), _jsx(Button, { variant: 'contained', startIcon: _jsx(Additem, {}), color: 'primary', size: 'large', onClick: handleNewKnowledgeBaseClick, children: t('newKnowledgeBase') })] }), _jsx(Box, { display: 'flex', justifyContent: 'flex-start', children: _jsx(TextField, { placeholder: t('searchKnowledgeBase'), value: searchTerm, onChange: (e) => setSearchTerm(e.target.value), sx: { width: { xs: '100%', sm: 400, md: 450 }, maxWidth: '100%' }, size: 'small', InputProps: {
                                startAdornment: (_jsx(InputAdornment, { position: 'start', children: _jsx(SearchIcon, {}) }))
                            } }) })] }), _jsx(Box, { width: '100%', mt: 1, children: _jsx(CustomGrid, { rows: filteredKnowledgeBases, columns: columns, sortModel: sortModel, onSortModelChange: setSortModel }) }), _jsx(NewKnowledgeBaseDialog, { openDialog: newKnowledgeBaseDialog, onClose: closeNewKnowledgeBaseDialog }), _jsx(EditKnowledgeBaseDialog, { openDialog: editKnowledgeBaseDialog, onClose: closeEditDialog, knowledgeBase: selectedKnowledgeBase }), _jsx(DeleteDialog, { open: isDeleteDialogOpen, proposalName: selectedKnowledgeBaseTitle ?? '', onClose: closeDeleteDialog, onDelete: handleDelete }), _jsx(KnowledgeBaseDocumentPopUp, { files: allKnowledgeBases?.find((kb) => kb.id === selectedKnowledgeBaseId)?.files || [], openDialog: viewDocumentsDialog, onClose: closeViewDocumentsDialog, title: selectedKnowledgeBaseTitle ? selectedKnowledgeBaseTitle : '' })] }));
};
export default KnowledgeBase;
